import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function AuthPage({ mode }) {
  const { user, authenticate } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isRegister = mode === "register";

  if (user) return <Navigate to={user.current_language ? "/dashboard" : "/languages"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const u = await authenticate(mode, payload);
      navigate(u.current_language ? "/dashboard" : "/languages");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const field = (key, label, type = "text") => (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <input
        data-testid={`auth-${key}-input`}
        type={type}
        required
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1.5 w-full rounded-xl border-2 border-slate-200 focus:border-[hsl(var(--primary))] focus:outline-none px-4 py-3 bg-white"
      />
    </label>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] grid place-items-center px-4 py-10">
      <div className="w-full max-w-md animate-fade-up">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <span className="h-10 w-10 rounded-xl bg-[hsl(var(--primary))] text-white grid place-items-center font-display font-extrabold">L</span>
          <span className="font-display font-extrabold text-xl">LinguaPath</span>
        </Link>
        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 shadow-sm p-7 sm:p-8 space-y-4" data-testid="auth-form">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{isRegister ? "Create your account" : "Welcome back"}</h1>
          <p className="text-slate-600 text-sm -mt-2">{isRegister ? "Your path starts with a single word." : "Pick up right where you left off."}</p>
          {isRegister && field("name", "Your name")}
          {field("email", "Email", "email")}
          {field("password", "Password", "password")}
          {error && <p data-testid="auth-error" className="text-sm text-rose-600 font-medium">{error}</p>}
          <button data-testid="auth-submit-button" disabled={loading} className="btn-primary w-full">
            {loading ? "Please wait..." : isRegister ? "Sign up" : "Log in"}
          </button>
          <p className="text-sm text-center text-slate-600">
            {isRegister ? "Already learning?" : "New here?"}{" "}
            <Link data-testid="auth-switch-link" to={isRegister ? "/login" : "/register"} className="font-bold text-[hsl(var(--primary-dark))]">
              {isRegister ? "Log in" : "Create an account"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
